import React, { useContext, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useParams } from 'react-router-dom'
import { Box, Typography } from '@mui/material_modern'
import { Grid } from '@mui/material'
import TabsLayoutContext from 'components/layouts/TabsLayoutContextProvider'
import ContentLayoutContext from 'components/layouts/ContentLayoutContextProvider'
import { useGetAssetByCategoryAndIdQuery, useGetAssetHistoryQuery } from 'services/physicalAssets'
import AssetHistoryGrid from 'components/generic/datagrid/AssetHistoryGrid.jsx'
import useGlobalLoader from 'utils/custom-hooks/useGlobalLoader'
import {
  DISPATCH_TICKET_TYPES,
  DISPATCH_TICKET_STATUSES,
  SORTABLE_ASSET_HISTORY_FIELDS,
  ASSET_CATEGORY,
} from 'utils/constants'
import { formatCustomerLocationString } from 'utils/data-manipulation/valueFormatter.jsx'
import { PAGE_SIZE } from 'utils/constants'

const AssetHistoryContainer = ({ assetCategory }) => {
  const { assetId } = useParams()
  const contentContext = useContext(ContentLayoutContext)
  const tabsContext = useContext(TabsLayoutContext)
  const [paginationOptions, setPaginationOptions] = useState({ page: 1, pageSize: PAGE_SIZE })
  const [filter, setFilter] = useState({
    search: '',
    sort: {
      field: SORTABLE_ASSET_HISTORY_FIELDS.LAST_UPDATED,
      ascending: false,
    },
  })

  useGlobalLoader(false)

  const { data: assetHistoryData, isFetching: isFetchingAssetHistoryData } = useGetAssetHistoryQuery(
    {
      assetCategory,
      assetId,
      filter: JSON.stringify(filter),
      ...paginationOptions,
    },
    { refetchOnMountOrArgChange: true },
  )

  const { data: assetData, isLoading: isLoadingAssetData } = useGetAssetByCategoryAndIdQuery({ assetCategory, assetId })

  const handlePageChange = newPageNumber => {
    setPaginationOptions(prevState => ({
      ...prevState,
      page: newPageNumber + 1,
    }))
  }

  const handlePageSizeChange = newPageSize => {
    setPaginationOptions(prevState => ({
      ...prevState,
      pageSize: newPageSize,
    }))
  }

  const transformedRows = useMemo(
    () =>
      (assetHistoryData?.Items || []).slice().map(assetHistory => ({
        id: assetHistory.dispatchTicket.id,
        ticketNumber: assetHistory.dispatchTicket.externalTicketId,
        lastUpdated: assetHistory.createdAt,
        status: DISPATCH_TICKET_STATUSES[assetHistory.dispatchTicket.status]?.id,
        statusName: DISPATCH_TICKET_STATUSES[assetHistory.dispatchTicket.status]?.name,
        ticketType: DISPATCH_TICKET_TYPES[assetHistory.dispatchTicket.type]?.name,
        daysOnSite: assetHistory.daysOnSite,
        fromLocation: formatCustomerLocationString(
          assetHistory.fromLocation?.customer?.displayName,
          assetHistory.fromLocation?.name,
        ),
        toLocation: formatCustomerLocationString(
          assetHistory.toLocation?.customer?.displayName,
          assetHistory.toLocation?.name,
        ),
      })),
    [assetHistoryData],
  )

  useEffect(() => {
    tabsContext.setContainerTabsVisibility(true)

    contentContext.setContainerTitle(
      <Box display="flex" alignItems="center">
        <Typography variant="h5" sx={{ flex: 1, pr: 2 }}>
          {assetData?.assetName || '---'}
        </Typography>
      </Box>,
    )

    contentContext.setContainerActions([])
  }, [assetData])

  return (
    <Grid p={3} sx={{ height: '100%' }} container flexDirection="column">
      <AssetHistoryGrid
        rows={transformedRows}
        loading={isFetchingAssetHistoryData || isLoadingAssetData}
        useBackendSortAndFilter
        filter={filter}
        setFilter={setFilter}
        paginationMode="server"
        rowCount={assetHistoryData?.totalResultsCount || 0}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        pageSize={paginationOptions.pageSize}
      />
    </Grid>
  )
}

AssetHistoryContainer.propTypes = {
  assetCategory: PropTypes.oneOf([ASSET_CATEGORY.TRUCK, ASSET_CATEGORY.TRAILER, ASSET_CATEGORY.CONTAINER]).isRequired,
}

export default AssetHistoryContainer
